import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { expenseService } from '@/services/expense.service';
import type {
  Expense,
  ExpenseFilters,
  CreateExpenseDto,
  UpdateExpenseDto,
  PaginatedExpensesResponse,
  UserBalance,
  Balance,
  SettleUpDto,
} from '@/types/expense.types';
import { ApiError } from '@/services/api';

// Expense state interface
interface ExpenseState {
  expenses: Expense[];
  currentExpense: Expense | null;
  balances: Balance[];
  myBalance: UserBalance | null;
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
  } | null;
  filters: ExpenseFilters;
  isLoading: boolean;
  isLoadingExpense: boolean;
  isLoadingBalances: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  isMarkingPaid: boolean;
  isSettlingUp: boolean;
  error: string | null;
}

// Initial state
const initialState: ExpenseState = {
  expenses: [],
  currentExpense: null,
  balances: [],
  myBalance: null,
  meta: null,
  filters: {
    page: 1,
    limit: 10,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  },
  isLoading: false,
  isLoadingExpense: false,
  isLoadingBalances: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  isMarkingPaid: false,
  isSettlingUp: false,
  error: null,
};

// Async thunks

// Fetch expenses list
export const fetchExpenses = createAsyncThunk<
  PaginatedExpensesResponse,
  ExpenseFilters | undefined,
  { rejectValue: string }
>('expenses/fetchExpenses', async (filters, { rejectWithValue, getState }) => {
  try {
    const state = getState() as { expenses: ExpenseState };
    const mergedFilters = { ...state.expenses.filters, ...filters };
    return await expenseService.getExpenses(mergedFilters);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden der Ausgaben');
  }
});

// Fetch single expense
export const fetchExpenseById = createAsyncThunk<
  Expense,
  string,
  { rejectValue: string }
>('expenses/fetchExpenseById', async (id, { rejectWithValue }) => {
  try {
    return await expenseService.getExpenseById(id);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden der Ausgabe');
  }
});

// Create expense
export const createExpense = createAsyncThunk<
  Expense,
  CreateExpenseDto,
  { rejectValue: string }
>('expenses/createExpense', async (data, { rejectWithValue }) => {
  try {
    return await expenseService.createExpense(data);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Erstellen der Ausgabe');
  }
});

// Update expense
export const updateExpense = createAsyncThunk<
  Expense,
  { id: string; data: UpdateExpenseDto },
  { rejectValue: string }
>('expenses/updateExpense', async ({ id, data }, { rejectWithValue }) => {
  try {
    return await expenseService.updateExpense(id, data);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Aktualisieren der Ausgabe');
  }
});

// Delete expense
export const deleteExpense = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>('expenses/deleteExpense', async (id, { rejectWithValue }) => {
  try {
    await expenseService.deleteExpense(id);
    return id;
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Loeschen der Ausgabe');
  }
});

// Mark split as paid
export const markSplitAsPaid = createAsyncThunk<
  { expenseId: string; userId: string },
  { expenseId: string; userId: string },
  { rejectValue: string }
>('expenses/markSplitAsPaid', async ({ expenseId, userId }, { rejectWithValue }) => {
  try {
    await expenseService.markSplitAsPaid(expenseId, userId);
    return { expenseId, userId };
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Markieren als bezahlt');
  }
});

// Fetch all balances
export const fetchBalances = createAsyncThunk<
  Balance[],
  void,
  { rejectValue: string }
>('expenses/fetchBalances', async (_, { rejectWithValue }) => {
  try {
    return await expenseService.getBalances();
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden der Bilanzen');
  }
});

// Fetch my balance
export const fetchMyBalance = createAsyncThunk<
  UserBalance,
  void,
  { rejectValue: string }
>('expenses/fetchMyBalance', async (_, { rejectWithValue }) => {
  try {
    return await expenseService.getMyBalance();
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden deiner Bilanz');
  }
});

// Settle up
export const settleUp = createAsyncThunk<
  SettleUpDto,
  SettleUpDto,
  { rejectValue: string }
>('expenses/settleUp', async (data, { rejectWithValue }) => {
  try {
    await expenseService.settleUp(data);
    return data;
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Ausgleichen');
  }
});

// Expense slice
const expenseSlice = createSlice({
  name: 'expenses',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentExpense: (state) => {
      state.currentExpense = null;
    },
    setFilters: (state, action: PayloadAction<Partial<ExpenseFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetFilters: (state) => {
      state.filters = {
        page: 1,
        limit: 10,
        sortBy: 'createdAt',
        sortOrder: 'desc',
      };
    },
    resetExpenses: () => initialState,
  },
  extraReducers: (builder) => {
    // Fetch expenses
    builder
      .addCase(fetchExpenses.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchExpenses.fulfilled, (state, action) => {
        state.isLoading = false;
        state.expenses = action.payload.expenses;
        state.meta = action.payload.meta;
      })
      .addCase(fetchExpenses.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? 'Fehler beim Laden';
      });

    // Fetch single expense
    builder
      .addCase(fetchExpenseById.pending, (state) => {
        state.isLoadingExpense = true;
        state.error = null;
      })
      .addCase(fetchExpenseById.fulfilled, (state, action) => {
        state.isLoadingExpense = false;
        state.currentExpense = action.payload;
      })
      .addCase(fetchExpenseById.rejected, (state, action) => {
        state.isLoadingExpense = false;
        state.error = action.payload ?? 'Fehler beim Laden';
      });

    // Create expense
    builder
      .addCase(createExpense.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createExpense.fulfilled, (state, action) => {
        state.isCreating = false;
        state.expenses.unshift(action.payload);
      })
      .addCase(createExpense.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload ?? 'Fehler beim Erstellen';
      });

    // Update expense
    builder
      .addCase(updateExpense.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateExpense.fulfilled, (state, action) => {
        state.isUpdating = false;
        const index = state.expenses.findIndex((e) => e.id === action.payload.id);
        if (index !== -1) {
          state.expenses[index] = action.payload;
        }
        if (state.currentExpense?.id === action.payload.id) {
          state.currentExpense = action.payload;
        }
      })
      .addCase(updateExpense.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload ?? 'Fehler beim Aktualisieren';
      });

    // Delete expense
    builder
      .addCase(deleteExpense.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deleteExpense.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.expenses = state.expenses.filter((e) => e.id !== action.payload);
        if (state.currentExpense?.id === action.payload) {
          state.currentExpense = null;
        }
      })
      .addCase(deleteExpense.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload ?? 'Fehler beim Loeschen';
      });

    // Mark split as paid
    builder
      .addCase(markSplitAsPaid.pending, (state) => {
        state.isMarkingPaid = true;
        state.error = null;
      })
      .addCase(markSplitAsPaid.fulfilled, (state, action) => {
        state.isMarkingPaid = false;
        const { expenseId, userId } = action.payload;

        // Update in expenses list
        const expenseIndex = state.expenses.findIndex((e) => e.id === expenseId);
        if (expenseIndex !== -1) {
          const splitIndex = state.expenses[expenseIndex].splits.findIndex(
            (s) => s.userId === userId
          );
          if (splitIndex !== -1) {
            state.expenses[expenseIndex].splits[splitIndex].isSettled = true;
            state.expenses[expenseIndex].splits[splitIndex].settledAt = new Date().toISOString();
          }
        }

        // Update in current expense
        if (state.currentExpense?.id === expenseId) {
          const splitIndex = state.currentExpense.splits.findIndex(
            (s) => s.userId === userId
          );
          if (splitIndex !== -1) {
            state.currentExpense.splits[splitIndex].isSettled = true;
            state.currentExpense.splits[splitIndex].settledAt = new Date().toISOString();
          }
        }
      })
      .addCase(markSplitAsPaid.rejected, (state, action) => {
        state.isMarkingPaid = false;
        state.error = action.payload ?? 'Fehler beim Markieren als bezahlt';
      });

    // Fetch balances
    builder
      .addCase(fetchBalances.pending, (state) => {
        state.isLoadingBalances = true;
        state.error = null;
      })
      .addCase(fetchBalances.fulfilled, (state, action) => {
        state.isLoadingBalances = false;
        state.balances = action.payload;
      })
      .addCase(fetchBalances.rejected, (state, action) => {
        state.isLoadingBalances = false;
        state.error = action.payload ?? 'Fehler beim Laden der Bilanzen';
      });

    // Fetch my balance
    builder
      .addCase(fetchMyBalance.pending, (state) => {
        state.isLoadingBalances = true;
        state.error = null;
      })
      .addCase(fetchMyBalance.fulfilled, (state, action) => {
        state.isLoadingBalances = false;
        state.myBalance = action.payload;
      })
      .addCase(fetchMyBalance.rejected, (state, action) => {
        state.isLoadingBalances = false;
        state.error = action.payload ?? 'Fehler beim Laden deiner Bilanz';
      });

    // Settle up
    builder
      .addCase(settleUp.pending, (state) => {
        state.isSettlingUp = true;
        state.error = null;
      })
      .addCase(settleUp.fulfilled, (state) => {
        state.isSettlingUp = false;
        // Balance will be refetched after settling
      })
      .addCase(settleUp.rejected, (state, action) => {
        state.isSettlingUp = false;
        state.error = action.payload ?? 'Fehler beim Ausgleichen';
      });
  },
});

// Export actions
export const {
  clearError,
  clearCurrentExpense,
  setFilters,
  resetFilters,
  resetExpenses,
} = expenseSlice.actions;

// Selectors
export const selectExpenses = (state: { expenses: ExpenseState }) => state.expenses.expenses;
export const selectCurrentExpense = (state: { expenses: ExpenseState }) => state.expenses.currentExpense;
export const selectBalances = (state: { expenses: ExpenseState }) => state.expenses.balances;
export const selectMyBalance = (state: { expenses: ExpenseState }) => state.expenses.myBalance;
export const selectExpensesMeta = (state: { expenses: ExpenseState }) => state.expenses.meta;
export const selectExpensesFilters = (state: { expenses: ExpenseState }) => state.expenses.filters;
export const selectExpensesLoading = (state: { expenses: ExpenseState }) => ({
  isLoading: state.expenses.isLoading,
  isLoadingExpense: state.expenses.isLoadingExpense,
  isLoadingBalances: state.expenses.isLoadingBalances,
  isCreating: state.expenses.isCreating,
  isUpdating: state.expenses.isUpdating,
  isDeleting: state.expenses.isDeleting,
  isMarkingPaid: state.expenses.isMarkingPaid,
  isSettlingUp: state.expenses.isSettlingUp,
});
export const selectExpensesError = (state: { expenses: ExpenseState }) => state.expenses.error;

// Export reducer
export default expenseSlice.reducer;
